import { useLocation } from "wouter";
import { useAuthMe } from "@vorloop/api-client-react";
import { NavRail } from "../components/layout/NavRail";
import { WorkspaceSwitcher } from "../components/layout/WorkspaceSwitcher";

/**
 * Import sources page — Replit-style grid of "bring code or designs
 * from somewhere else" cards. Each card is a stub for now; real wiring
 * lands when the providers ship (GitHub OAuth, Figma plugin, etc.).
 */

interface ImportSource {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly badge?: "FREE";
  readonly swatch: string;
  readonly initial: string;
  readonly status: "stub" | "built";
  readonly note?: string;
}

const SOURCES: ReadonlyArray<ImportSource> = [
  {
    id: "github",
    title: "GitHub",
    description: "Import any repository or existing app. Agent may be less predictable.",
    swatch: "#0f172a",
    initial: "⌥",
    status: "stub",
  },
  {
    id: "bitbucket",
    title: "Bitbucket",
    description: "Import a repository or existing app. Agent support may be limited.",
    swatch: "#1c4eaa",
    initial: "▭",
    status: "stub",
  },
  {
    id: "figma",
    title: "Figma Design",
    description: "Convert your designs into live Apps using the Vor Agent",
    swatch: "linear-gradient(180deg, #f24e1e, #a259ff)",
    initial: "✦",
    status: "stub",
  },
  {
    id: "lovable",
    title: "Lovable",
    badge: "FREE",
    description: "Migrate your site to make it production-ready",
    swatch: "linear-gradient(135deg, #ff6b6b, #fb7185)",
    initial: "♥",
    status: "stub",
  },
  {
    id: "bolt",
    title: "Bolt",
    description: "Migrate your prototype to make it production-ready",
    swatch: "#facc15",
    initial: "⚡",
    status: "stub",
  },
  {
    id: "base44",
    title: "Base44",
    badge: "FREE",
    description: "Migrate your site to make it production-ready",
    swatch: "#0ea5e9",
    initial: "▤",
    status: "stub",
  },
  {
    id: "vercel",
    title: "Vercel",
    badge: "FREE",
    description: "Migrate your site to make it production-ready",
    swatch: "#000000",
    initial: "▲",
    status: "stub",
  },
  {
    id: "spreadsheet",
    title: "Spreadsheet",
    description: "Create an app from Excel, CSV, or Google Sheets data",
    swatch: "#22c55e",
    initial: "▦",
    status: "stub",
  },
  {
    id: "zip",
    title: "Zip file",
    description: "Import from a .zip file.",
    swatch: "#a78bfa",
    initial: "▣",
    status: "stub",
  },
  {
    id: "empty",
    title: "Empty",
    description: "Start from a completely empty project without Agent setup or scaffolding.",
    swatch: "#64748b",
    initial: "◇",
    status: "built",
    note: "Mock — bypasses Agent scaffolding; real /api/projects?type=empty ships in Wave 2a.",
  },
];

export function Import() {
  const [, setLocation] = useLocation();
  const me = useAuthMe();
  const handle = me.data?.user?.handle ?? "you";

  function pick(s: ImportSource) {
    if (s.id === "empty") {
      // Bounce to the dashboard with a "start blank" intent — actual
      // empty-project endpoint will pick this up when it ships.
      setLocation("/?intent=empty");
      return;
    }
    alert(
      `Mock — ${s.title} import flow. Real wiring lands with the per-source provider (see HANDOFF.md §15).`,
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "var(--bg-canvas)" }}>
      <NavRail handle={handle} />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 24px",
          }}
        >
          <WorkspaceSwitcher handle={handle} compact />
        </header>

        <div style={{ flex: 1, padding: "20px 48px 80px" }}>
          <div style={{ maxWidth: 980, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>
            <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Import to Vor
              </h1>
              <p style={{ margin: 0, color: "var(--text-tertiary)", fontSize: 13 }}>
                Migrate data, code, and designs from other apps into Vor
              </p>
            </header>

            <div
              data-testid="import-sources"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: 10,
              }}
            >
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s)}
                  data-testid={`import-source-${s.id}`}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    padding: "14px 16px",
                    background: "var(--bg-panel)",
                    border: "1px solid var(--border-default)",
                    borderRadius: 12,
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-panel-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-panel)")}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: s.swatch,
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {s.initial}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{s.title}</span>
                      {s.badge === "FREE" && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: 0.6,
                            color: "#34d399",
                            background: "rgba(16,185,129,0.12)",
                            padding: "1px 6px",
                            borderRadius: 999,
                          }}
                        >
                          FREE
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 12.5, color: "var(--text-tertiary)", lineHeight: 1.45 }}>
                      {s.description}
                    </span>
                  </span>
                  <span aria-hidden style={{ color: "var(--text-tertiary)", fontSize: 14, marginTop: 2 }}>
                    →
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
